import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, Video, ShieldCheck, Bell, Smartphone, Zap, CheckCircle, ArrowRight, AlertTriangle, Eye, Volume2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimatedSection } from '../hooks/useScrollAnimation';

const AISecurityPage = () => {
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const enableSound = () => {
      if (videoRef.current && !soundEnabled) {
        videoRef.current.muted = false;
        videoRef.current.play().catch(() => {});
        setSoundEnabled(true);
      }
    };

    document.addEventListener('click', enableSound, { once: true });
    return () => document.removeEventListener('click', enableSound);
  }, [soundEnabled]);

  const steps = [
    {
      num: '01',
      icon: Eye,
      title: 'Détection IA',
      desc: 'Les caméras Ajax analysent l\'image en temps réel et détectent les humains ou véhicules grâce à l\'intelligence artificielle embarquée.'
    },
    {
      num: '02',
      icon: Zap,
      title: 'Déclenchement automatique',
      desc: 'Dès qu\'une personne ou un véhicule est détecté, le système envoie un signal au Hub Ajax pour déclencher l\'alarme.'
    },
    {
      num: '03',
      icon: Bell,
      title: 'Alerte sirène',
      desc: 'Les sirènes intérieures et extérieures se déclenchent automatiquement pour dissuader l\'intrus et alerter le voisinage.'
    },
    {
      num: '04',
      icon: Smartphone,
      title: 'Notification instantanée',
      desc: 'Vous recevez une notification push avec la photo de l\'intrus et pouvez visionner le flux vidéo en direct depuis l\'application Ajax.'
    },
  ];

  const benefits = [
    'Zéro fausse alarme grâce à la détection IA',
    'Dissuasion maximale avec déclenchement sirène',
    'Preuve vidéo en cas d\'intrusion',
    'Notification instantanée avec photo',
    'Vérification à distance en temps réel',
    'Fonctionne même sans connexion internet (alarme locale)',
  ];

  const useCases = [
    {
      title: 'Maison individuelle',
      desc: 'Protégez votre jardin, entrée et garage. Détection des intrusions avant même l\'entrée dans la maison.',
      icon: '🏡'
    },
    {
      title: 'Commerce',
      desc: 'Surveillance extérieure et intérieure. Détection des rodeurs et déclenchement dissuasif.',
      icon: '🏪'
    },
    {
      title: 'Entrepôt',
      desc: 'Surveillance périmétrique de grandes surfaces. Détection des véhicules non autorisés.',
      icon: '🏭'
    },
    {
      title: 'Résidence secondaire',
      desc: 'Protection à distance avec alertes instantanées. Vérifiez à tout moment l\'état de votre propriété.',
      icon: '🏠'
    },
  ];

  return (
    <div className="min-h-screen py-12 bg-black">
      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="relative py-16 md:py-20 rounded-2xl md:rounded-3xl overflow-hidden bg-[#0a0a0a] border border-white/5">
          <div className="relative max-w-6xl mx-auto px-4 md:px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div className="text-center lg:text-left">
                <p className="text-purple-400 text-sm font-medium tracking-wide uppercase mb-4">Innovation FGS Alarme Montpellier</p>
                
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-6 tracking-tight">
                  Protection par IA
                </h1>
                
                <p className="text-white/60 text-base md:text-lg mb-8 font-light leading-relaxed">
                  La révolution de la sécurité : nos caméras intelligentes <span className="text-emerald-400">détectent les intrus</span> et 
                  <span className="text-purple-400"> déclenchent automatiquement</span> votre système d'alarme Ajax.
                </p>
                
                <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                  <Link to="/video">
                    <Button size="lg" className="bg-white text-black hover:bg-white/90 h-14 px-8 rounded-full font-medium">
                      <Video className="w-5 h-5 mr-2" />
                      Voir les caméras IA
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 h-14 px-8 rounded-full">
                      Demander un devis
                    </Button>
                  </Link>
                </div>
              </div>
              
              {/* Video / Partner Logo */}
              <div className="relative">
                <div className="rounded-2xl overflow-hidden border border-white/10 relative bg-black">
                  {!videoEnded ? (
                    <>
                      <video 
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        className="w-full aspect-video"
                        onEnded={() => setVideoEnded(true)}
                      >
                        <source src="/videos/ajax-protection-ia.mp4" type="video/mp4" />
                      </video>
                      {!soundEnabled && (
                        <button 
                          onClick={() => {
                            if (videoRef.current) {
                              videoRef.current.muted = false;
                              setSoundEnabled(true);
                            }
                          }}
                          className="absolute bottom-4 right-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2 transition-all text-sm font-medium border border-white/10"
                        >
                          <Volume2 className="w-4 h-4" />
                          Activer le son
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="aspect-video flex flex-col items-center justify-center p-8 bg-[#0a0a0a]">
                      <div className="text-4xl font-heading font-semibold text-white mb-4 tracking-wider">
                        AJAX
                      </div>
                      <p className="text-white/50 text-sm">FGS Alarme Montpellier — Installateur certifié</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <AnimatedSection>
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-20">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
            Comment ça fonctionne ?
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto font-light">
            L'intégration unique entre vidéoprotection IA et système d'alarme Ajax
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((step, i) => (
            <div 
              key={i} 
              className="relative bg-white/[0.02] border border-white/5 hover:border-purple-500/30 rounded-2xl p-6 transition-colors"
            >
              <div className="font-mono text-5xl font-bold text-purple-500/10 absolute top-4 right-4">
                {step.num}
              </div>
              <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center mb-4">
                <step.icon className="w-5 h-5 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>
      </AnimatedSection>

      {/* Visual Schema */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-20">
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-8 md:p-12">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            {/* Camera */}
            <div className="text-center">
              <div className="w-24 h-24 mx-auto rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center mb-4">
                <Video className="w-12 h-12 text-purple-400" />
              </div>
              <h4 className="font-heading text-lg font-bold text-white mb-2">Caméra IA</h4>
              <p className="text-sm text-white/60">Détecte humain ou véhicule</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center">
              <div className="flex items-center gap-4">
                <Zap className="w-8 h-8 text-emerald-400 animate-pulse" />
                <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-fgs-primary rounded-full" />
                <Zap className="w-8 h-8 text-purple-400 animate-pulse" />
              </div>
            </div>

            {/* Alarm */}
            <div className="text-center">
              <div className="w-24 h-24 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
                <ShieldCheck className="w-12 h-12 text-emerald-400" />
              </div>
              <h4 className="font-heading text-lg font-bold text-white mb-2">Système Ajax</h4>
              <p className="text-sm text-white/60">Déclenche alarme + sirènes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-semibold text-white uppercase mb-6">
              Les avantages de l'intégration IA + Alarme
            </h2>
            <ul className="space-y-4">
              {benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80 text-lg">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-fgs-accent/20 to-transparent border border-fgs-accent/30 rounded-xl p-8">
            <AlertTriangle className="w-12 h-12 text-fgs-accent mb-4" />
            <h3 className="text-2xl font-semibold text-white mb-4">
              Pourquoi c'est révolutionnaire ?
            </h3>
            <p className="text-white/70 mb-6">
              Traditionnellement, vidéosurveillance et alarme sont deux systèmes séparés. 
              Avec Ajax, ils communiquent ensemble : la caméra devient un détecteur intelligent 
              capable de déclencher toute la chaîne de sécurité.
            </p>
            <p className="text-white font-medium">
              Résultat : une détection plus précoce, une dissuasion maximale et des preuves vidéo systématiques.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-semibold text-white uppercase mb-4">
            Pour qui ?
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {useCases.map((useCase, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl p-6 hover:border-fgs-primary/50 transition-colors">
              <span className="text-4xl mb-4 block">{useCase.icon}</span>
              <h3 className="text-xl font-semibold text-white mb-2">{useCase.title}</h3>
              <p className="text-white/60 text-sm">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="bg-gradient-to-r from-purple-900/40 to-fgs-primary/40 rounded-2xl p-8 md:p-16 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase mb-6">
            Passez à la sécurité intelligente
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
            Contactez-nous pour une étude personnalisée de votre projet de vidéoprotection + alarme IA
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/video">
              <Button size="lg" className="bg-purple-600 hover:bg-purple-700 h-14 px-8">
                Voir les caméras
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/shop">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-500-hover h-14 px-8">
                Voir les alarmes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white/30 text-white h-14 px-8">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AISecurityPage;
